/**
 * "Can this user actually use the app, or do they still owe us a profile?"
 *
 * Completeness used to mean "a profile row exists", which let a social sign-in
 * whose row was created by the old signup trigger stay inside the app forever
 * without a username — the identity the app builds profile links, student lists
 * and partner suggestions from.
 *
 * The missing piece is detected by PROVIDER, never by a missing username alone:
 * the column was added to existing profiles, so every profile older than it has
 * a NULL username. Judging by the username alone would drag those legacy email
 * users to /complete-profile too.
 *
 * A provider we cannot read is treated as email — the conservative side, since
 * missing information must not push anyone into a form they don't need.
 */

export interface ProfileCompletenessInput {
  /** Whether a profiles row exists for the authenticated user. */
  hasRow: boolean;
  /** The stored username, or null when the profile never got one. */
  username: string | null;
  /** `app_metadata.provider` from the session: 'email', 'google', … */
  provider: string | null | undefined;
}

const EMAIL_PROVIDER = 'email';

export const isProfileComplete = ({ hasRow, username, provider }: ProfileCompletenessInput): boolean => {
  if (!hasRow) return false;
  if (username && username.trim() !== '') return true;
  return !provider || provider === EMAIL_PROVIDER;
};
